import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { OpenBadge } from "./OpenHours";
import { EASE } from "./Reveal";
import { useLang, scrollToId } from "../../lib/lang";
import { INFO } from "../../lib/content";

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.25 + i * 0.13 }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} id="top" data-testid="hero-section" onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden bg-[#1a0508]">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[6%] h-[115%]">
        <motion.img
          src="/food/courtyard.jpg"
          alt="Levantisk innergård"
          className="h-full w-full object-cover object-[center_60%]"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.04 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#2b0d0a]/75 via-[#5a2219]/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2b0d0a]/80 via-transparent to-[#2b0d0a]/30" />
      <div className="pattern-overlay absolute inset-0 opacity-[0.07]" />

      <div className="relative z-10 mx-auto grid max-w-[1400px] grid-cols-12 items-center gap-y-12 px-5 pb-20 pt-32 lg:gap-x-12 lg:px-10 lg:pt-36">
        <div className="col-span-12 lg:col-span-7">
          <div className="mb-5 sm:hidden"><OpenBadge light /></div>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="eyebrow flex items-center gap-3 text-[#E9C46A]">
            <span className="h-px w-10 bg-[#E9C46A]" /> {t.hero.eyebrow}
          </motion.p>
          <h1 key={lang} data-testid="hero-headline" className="font-display mt-6 text-[3.1rem] leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-[6.2rem]">
            <MaskLine i={0}>{t.hero.lines[0]}</MaskLine>
            <MaskLine i={1} className="italic text-[#E9C46A]">{t.hero.lines[1]}</MaskLine>
            <MaskLine i={2} className="text-[0.62em] leading-[1.05] text-white/90">{t.hero.lines[2]}</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75, ease: EASE }} className="mt-7 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease: EASE }} className="mt-9 flex flex-wrap items-center gap-4">
            <button data-testid="hero-menu-button" onClick={() => scrollToId("menu")} className="btn-gold group">
              {t.hero.ctaMenu} <ArrowDown size={17} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a data-testid="hero-call-button" href={INFO.phoneHref} className="btn-ghost-light"><Phone size={16} /> {t.hero.ctaCall}</a>
          </motion.div>
        </div>

        <motion.div
          className="col-span-12 lg:col-span-5 [perspective:1400px]"
          initial={{ y: 40 }}
          animate={{ y: 0 }}
          transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
        >
          <motion.div style={{ rotateX: rx, rotateY: ry }} className="[transform-style:preserve-3d]">
            <ContactForm />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
